import { useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { useGetCohortSummaryByCohortProfileIdQuery } from '@verantos-dev/data-access-cohort-summary';
import { useCohortBuilderData } from '@verantos-dev/data-access-cohorts';
import { CohortCharacteristicsResultType } from '@verantos-dev/types-cohorts';

import { sanitizeAgeAtIndexData, sanitizeGenderData } from '../helpers';
import { useCohortCharacteristicsTabularDataDialog } from './useCohortCharacteristicsTabularDataDialog';

export const useCohortCharacteristics = ({
  projectId,
  cohortId,
}: {
  projectId: string;
  cohortId: string;
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const { displayDataDialog, onHideDataDialog, onShowDataDialog } =
    useCohortCharacteristicsTabularDataDialog();

  const {
    isLoading,
    isUninitialized,
    error,
    data: cohortSummaryData,
  } = useGetCohortSummaryByCohortProfileIdQuery(
    { projectId, cohortProfileId: cohortId },
    { skip: !projectId || !cohortId }
  );

  const { data: cohortData } = useCohortBuilderData();
  const latestCohortStatus = cohortData?.data?.latestStatus;

  const ethnicityData = cohortSummaryData?.data?.[CohortCharacteristicsResultType.ETHNICITY];
  const genderData = sanitizeGenderData(
    cohortSummaryData?.data?.[CohortCharacteristicsResultType.GENDER]
  );
  const raceData = cohortSummaryData?.data?.[CohortCharacteristicsResultType.RACE];
  const ageAtIndexData = sanitizeAgeAtIndexData(
    cohortSummaryData?.data?.[CohortCharacteristicsResultType.AGE_AT_INDEX]
  );

  const totalSubjectCount = cohortData?.data?.latestSubjectCount || 0;

  const cohortMessages = {
    title: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortCharacteristicsPageTab.title',
    }),
    processingTitle: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortCharacteristicsPageTab.processing.title',
    }),
    processingMessage: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortCharacteristicsPageTab.processing.message',
    }),
    draftTitle: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortCharacteristicsPageTab.draft.title',
    }),
    draftMessage: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortCharacteristicsPageTab.draft.message',
    }),
    errorTitle: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortCharacteristicsPageTab.error.title',
    }),
    errorMessage: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortCharacteristicsPageTab.error.message',
    }),
    loadingTitle: intl.formatMessage({
      id: 'projects.cohortBuilder.cohortCharacteristicsPageTab.loading.title',
    }),
  };

  return {
    theme,
    latestCohortStatus,
    characteristicsError: error,
    isCohortSummaryInitiallyLoading: isLoading || isUninitialized,

    ethnicityData,
    genderData,
    raceData,
    ageAtIndexData,

    displayDataDialog,
    onHideDataDialog,
    onShowDataDialog,
    totalSubjectCount,
    cohortSummaryData: cohortSummaryData?.data,
    hasSummaryData: !!ethnicityData || !!genderData || !!raceData || !!ageAtIndexData,
    cohortMessages,
  };
};
